import React from "react";
import {
  Trophy,
  Users,
  Heart,
  ShieldCheck,
  Target,
  MapPin,
  Phone,
} from "lucide-react";

function About() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-950 via-red-950 to-black px-4 py-12 text-white sm:px-6 md:py-16">

      {/* Background Effects */}
      <div className="pointer-events-none fixed left-0 top-40 h-72 w-72 rounded-full bg-red-600/10 blur-3xl" />
      <div className="pointer-events-none fixed bottom-20 right-0 h-72 w-72 rounded-full bg-orange-500/10 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-6xl">

        {/* Hero */}
        <section className="mx-auto max-w-4xl text-center">

          <div className="mb-5 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-yellow-400/20 bg-yellow-400/10">
              <Trophy className="h-8 w-8 text-yellow-400" />
            </div>
          </div>

          <p className="mb-3 text-sm font-bold uppercase tracking-[0.3em] text-orange-400">
            BPL Season 2
          </p>

          <h1 className="text-4xl font-black tracking-tight sm:text-5xl md:text-6xl">
            About{" "}
            <span className="bg-gradient-to-r from-yellow-300 via-orange-400 to-red-500 bg-clip-text text-transparent">
              Badgaw Premier League
            </span>
          </h1>

          <p className="mx-auto mt-6 max-w-3xl text-base leading-7 text-gray-300 sm:text-lg sm:leading-8">
            Badgaw Premier League is a community-driven cricket tournament
            created to bring together talented players, passionate cricket
            fans, and the entire Badgaw community through the spirit of
            competitive cricket.
          </p>

        </section>

        {/* Tournament Overview */}
        <section className="mt-12 md:mt-16">

          <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.06] shadow-2xl backdrop-blur-md">

            <div className="grid md:grid-cols-2">

              {/* Left */}
              <div className="p-7 sm:p-10">

                <div className="mb-5 flex items-center gap-3">
                  <div className="rounded-xl bg-red-500/10 p-3">
                    <Trophy className="h-6 w-6 text-orange-400" />
                  </div>

                  <h2 className="text-2xl font-bold sm:text-3xl">
                    Our Journey
                  </h2>
                </div>

                <p className="text-base leading-7 text-gray-300 sm:text-lg">
                  BPL started with a simple vision — to create a platform
                  where cricket lovers and talented players could come
                  together and experience the excitement of competitive
                  cricket.
                </p>

                <p className="mt-4 text-base leading-7 text-gray-300 sm:text-lg">
                  Over time, the tournament has grown into a much-awaited
                  sporting event, bringing teams, players, supporters, and
                  the local community together.
                </p>

                <p className="mt-4 text-base leading-7 text-gray-300 sm:text-lg">
                  With{" "}
                  <span className="font-bold text-yellow-400">
                    BPL Season 2
                  </span>
                  , we aim to make the tournament even more exciting while
                  continuing to promote teamwork, sportsmanship, and cricketing
                  talent.
                </p>

              </div>

              {/* Right */}
              <div className="flex flex-col justify-center bg-gradient-to-br from-red-700/80 via-red-600/60 to-orange-500/60 p-7 sm:p-10">

                <p className="text-sm font-bold uppercase tracking-[0.25em] text-yellow-300">
                  Season 2
                </p>

                <h3 className="mt-3 text-3xl font-black sm:text-4xl">
                  More Cricket.
                  <br />
                  More Competition.
                  <br />
                  More Passion.
                </h3>

                <p className="mt-5 leading-7 text-red-50">
                  BPL is more than a tournament. It is a celebration of
                  cricket, friendship, competition, and community spirit.
                </p>

                <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-yellow-200">
                  <MapPin className="h-5 w-5" />
                  Badgaw, Bihar
                </div>

              </div>

            </div>

          </div>

        </section>

        {/* Values */}
        <section className="mt-12 md:mt-16">

          <div className="mb-8 text-center">

            <p className="text-sm font-bold uppercase tracking-[0.25em] text-orange-400">
              What BPL Represents
            </p>

            <h2 className="mt-2 text-3xl font-black sm:text-4xl">
              Cricket Beyond The Game
            </h2>

          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">

            {/* Talent */}
            <div className="group rounded-2xl border border-white/10 bg-white/[0.06] p-6 text-center backdrop-blur-md transition duration-300 hover:-translate-y-1 hover:border-orange-400/30 hover:bg-white/[0.09]">

              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-500/10">
                <Users className="h-7 w-7 text-blue-400" />
              </div>

              <h3 className="mt-5 text-lg font-bold">
                Great Players
              </h3>

              <p className="mt-2 text-sm leading-6 text-gray-400">
                Creating opportunities for talented cricketers to showcase
                their skills.
              </p>

            </div>

            {/* Community */}
            <div className="group rounded-2xl border border-white/10 bg-white/[0.06] p-6 text-center backdrop-blur-md transition duration-300 hover:-translate-y-1 hover:border-orange-400/30 hover:bg-white/[0.09]">

              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-green-500/10">
                <Heart className="h-7 w-7 text-green-400" />
              </div>

              <h3 className="mt-5 text-lg font-bold">
                Community
              </h3>

              <p className="mt-2 text-sm leading-6 text-gray-400">
                Bringing players, families, supporters, and cricket lovers
                together.
              </p>

            </div>

            {/* Sportsmanship */}
            <div className="group rounded-2xl border border-white/10 bg-white/[0.06] p-6 text-center backdrop-blur-md transition duration-300 hover:-translate-y-1 hover:border-orange-400/30 hover:bg-white/[0.09]">

              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-yellow-500/10">
                <ShieldCheck className="h-7 w-7 text-yellow-400" />
              </div>

              <h3 className="mt-5 text-lg font-bold">
                Sportsmanship
              </h3>

              <p className="mt-2 text-sm leading-6 text-gray-400">
                Promoting fair play, respect, discipline, and healthy
                competition.
              </p>

            </div>

            {/* Vision */}
            <div className="group rounded-2xl border border-white/10 bg-white/[0.06] p-6 text-center backdrop-blur-md transition duration-300 hover:-translate-y-1 hover:border-orange-400/30 hover:bg-white/[0.09]">

              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500/10">
                <Target className="h-7 w-7 text-red-400" />
              </div>

              <h3 className="mt-5 text-lg font-bold">
                Our Vision
              </h3>

              <p className="mt-2 text-sm leading-6 text-gray-400">
                Building a memorable cricket platform for players and fans
                year after year.
              </p>

            </div>

          </div>

        </section>

        {/* Season 2 CTA */}
        <section className="mt-12 md:mt-16">

          <div className="relative overflow-hidden rounded-3xl border border-orange-400/20 bg-gradient-to-r from-red-700 via-red-600 to-orange-500 p-8 text-center shadow-2xl sm:p-10">

            <div className="pointer-events-none absolute -right-20 -top-20 h-60 w-60 rounded-full bg-yellow-300/10 blur-3xl" />

            <Trophy className="mx-auto h-10 w-10 text-yellow-300" />

            <h2 className="relative mt-4 text-2xl font-black sm:text-3xl md:text-4xl">
              Be Part of BPL Season 2
            </h2>

            <p className="relative mx-auto mt-3 max-w-2xl text-sm leading-6 text-red-50 sm:text-base">
              Whether you're a player, supporter, or sponsor, BPL welcomes
              everyone who shares the passion for cricket.
            </p>

            <a
              href="/form"
              className="relative mt-6 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 font-bold text-red-600 shadow-lg transition hover:-translate-y-1 hover:shadow-xl"
            >
              Register Now
            </a>

          </div>

        </section>

        {/* Contact */}
        <section className="mt-10 text-center">

          <p className="text-sm font-semibold text-gray-400">
            Interested in sponsoring or partnering with BPL?
          </p>

          <a
            href="tel:+919973532109"
            className="mt-2 inline-flex items-center gap-2 text-lg font-bold text-orange-400 transition hover:text-yellow-300"
          >
            <Phone className="h-5 w-5" />
            +91 9973532109
          </a>

        </section>

      </div>
    </main>
  );
}

export default About;